import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Header from "../../components/landing/Header";
import Footer from "../../components/landing/Footer";
import { useTranslation } from "../../contexts/TranslationContext";
import ApiService from "../../services/ApiService";
import Loader from "../../components/Loader";
import ImageUpload from "../../components/ImageUpload";
import FileDropzone from "../../components/FileDropzone";

import heroImg from "../../assets/images/header-bg.png";

import "../../assets/css/landing.css";

const COUNTRY_CODES = [
  { flag: "🇦🇪", code: "+971" },
  { flag: "🇸🇦", code: "+966" },
  { flag: "🇶🇦", code: "+974" },
  { flag: "🇰🇼", code: "+965" },
  { flag: "🇧🇭", code: "+973" },
  { flag: "🇴🇲", code: "+968" },
];

const MAX_FILE_SIZE_MB = 2;

const INITIAL_FORM = {
  company_name: "",
  business_category: "",
  business_type: "",
  about: "",
  fullName: "",
  email: "",
  countryCode: "+971",
  phone: "",
  password: "",
  confirm_password: "",
  // Bank Details
  account_title: "",
  account_number: "",
  iban: "",
  bank_name: "",
  city: "",
};

const INITIAL_DOCUMENTS = {
  business_license: null,
  emirates_id: null,
  trade_name_certificate: null,
  logo: null,
  cover_images: [],
};

const DOCUMENTS = [
  { key: "business_license", required: true },
  { key: "emirates_id", required: true },
  { key: "trade_name_certificate" },
];

const BecomePartner = () => {
  const { isRTL, t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [dropdownData, setDropdownData] = useState({ categories: [], businessTypes: [] });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const res = await ApiService.request({
        method: 'GET',
        url: 'partner/getDropdownData',
      });
      if (res.data.status) {
        setDropdownData({
          categories: res.data.data?.categories || [],
          businessTypes: res.data.data?.business_types || [],
        });
      }
    } catch { /* silent */ }
  };

  const [form, setForm] = useState(INITIAL_FORM);

  const [documents, setDocuments] = useState(INITIAL_DOCUMENTS);

  const fileRefs = {
    business_license: useRef(null),
    emirates_id: useRef(null),
    trade_name_certificate: useRef(null),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (name, file) => {
    setDocuments(prev => ({ ...prev, logo: file }));
  };

  const setCoverImages = (files) => {
    setDocuments((prev) => {
      const currentCoverImages = Array.isArray(prev.cover_images) ? prev.cover_images : [];
      const nextCoverImages = typeof files === "function" ? files(currentCoverImages) : files;

      return {
        ...prev,
        cover_images: Array.isArray(nextCoverImages) ? nextCoverImages : [],
      };
    });
  };

  const handleFile = (key, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(t("becomePartner.toasts.fileTooLarge"));
      return;
    }
    setDocuments((prev) => ({ ...prev, [key]: file }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.company_name || !form.fullName || !form.email || !form.phone || !form.password) {
        toast.error(t("becomePartner.toasts.requiredFields"));
        return false;
      }
      if (form.password !== form.confirm_password) {
        toast.error(t("becomePartner.toasts.passwordMismatch"));
        return false;
      }
      return true;
    }
    if (step === 2) {
      if (!documents.business_license || !documents.emirates_id) {
        toast.error(t("becomePartner.toasts.requiredDocuments"));
        return false;
      }
      return true;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const prevStep = () => {
    setStep((s) => s - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.account_title || !form.account_number || !form.iban || !form.bank_name) {
        toast.error(t("becomePartner.toasts.bankDetails"));
        return;
    }

    setLoading(true);
    try {
      const fd = new FormData();
      // Step 1: Business Info
      fd.append('company_name', form.company_name);
      fd.append('business_category', form.business_category);
      fd.append('business_type', form.business_type);
      fd.append('about', form.about);
      fd.append('owner_name', form.fullName);
      fd.append('email', form.email);
      fd.append('phone_code', form.countryCode);
      fd.append('phone', form.phone);
      fd.append('password', form.password);
      fd.append('confirm_password', form.confirm_password);

      // Step 2: Documents
      if (documents.business_license) fd.append('business_license', documents.business_license);
      if (documents.emirates_id) fd.append('emirates_id', documents.emirates_id);
      if (documents.trade_name_certificate) fd.append('trade_name_certificate', documents.trade_name_certificate);
      if (documents.logo) fd.append('logo', documents.logo);
      const coverImages = Array.isArray(documents.cover_images) ? documents.cover_images : [];
      if (coverImages.length > 0) {
        coverImages.forEach(file => {
          fd.append('cover_images', file);
        });
      }

      // Step 3: Bank Details
      fd.append('account_title', form.account_title);
      fd.append('account_number', form.account_number);
      fd.append('iban', form.iban);
      fd.append('bank_name', form.bank_name);
      fd.append('city', form.city);

      const res = await ApiService.request({
        method: 'POST',
        url: 'partner/register',
        data: fd
      });

      if (res.data.status) {
        // Clear all form data and reset to the first step
        setForm(INITIAL_FORM);
        setDocuments(INITIAL_DOCUMENTS);
        setStep(1);
        // Show the success modal
        setShowSuccessModal(true);
      } else {
        toast.error(res.data.message || t("becomePartner.toasts.failed"));
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || t("becomePartner.toasts.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate("/");
  };

  return (
    <>
      <Header />
      <section className="partner-signup" dir={isRTL ? "rtl" : "ltr"}>
        <div className="partner-signup-grid">
          {/* Left: form */}
          <div className="partner-form-col">
            <div className="partner-form-card">
              <h2 className="partner-title">{t("becomePartner.title")}</h2>
              <p className="partner-subtitle">
                {t("becomePartner.subtitle")}
              </p>

              {/* Progress Bar */}
              <div className="partner-steps">
                <div className={`partner-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                    <div className="partner-step-icon">{step > 1 ? <i className="bi bi-check text-white" /> : "1"}</div>
                    <div className="partner-step-label">{t("becomePartner.steps.business")}</div>
                </div>
                <div className={`partner-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                    <div className="partner-step-icon">{step > 2 ? <i className="bi bi-check text-white" /> : "2"}</div>
                    <div className="partner-step-label">{t("becomePartner.steps.documents")}</div>
                </div>
                <div className={`partner-step ${step >= 3 ? 'active' : ''}`}>
                    <div className="partner-step-icon">3</div>
                    <div className="partner-step-label">{t("becomePartner.steps.bank")}</div>
                </div>
              </div>

              {/* Step 1: Business Info */}
              {step === 1 && (
                <div className="step-content animate__animated animate__fadeIn">
                  <h5 className="partner-section-title">{t("becomePartner.businessInfo")}</h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-person" />
                        <input
                          type="text"
                          name="company_name"
                          placeholder={`${t("becomePartner.businessName")} *`}
                          value={form.company_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-building" />
                        <select
                          name="business_category"
                          value={form.business_category}
                          onChange={handleChange}
                          className={form.business_category ? "" : "placeholder"}
                        >
                          <option value="" disabled>{t("becomePartner.category")}</option>
                          {dropdownData.categories.map((c) => (
                            <option key={c._id || c} value={c._id || c}>{(isRTL ? c.title_ar : c.title) || c.title || c}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="partner-input">
                        <i className="bi bi-building" />
                        <select
                          name="business_type"
                          value={form.business_type}
                          onChange={handleChange}
                          className={form.business_type ? "" : "placeholder"}
                        >
                          <option value="" disabled>{t("becomePartner.businessType")}</option>
                          {dropdownData.businessTypes.map((tp) => (
                            <option key={tp._id || tp} value={tp._id || tp}>{(isRTL ? tp.title_ar : tp.title) || tp.title || tp}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-12">
                      <textarea
                        className="partner-textarea"
                        name="about"
                        rows={3}
                        placeholder={t("becomePartner.aboutPlaceholder")}
                        value={form.about}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <h5 className="partner-section-title mt-4">{t("becomePartner.accountInfo")}</h5>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-person" />
                        <input
                          type="text"
                          name="fullName"
                          placeholder={`${t("becomePartner.ownerName")} *`}
                          value={form.fullName}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-envelope" />
                        <input
                          type="email"
                          name="email"
                          placeholder={`${t("becomePartner.email")} *`}
                          value={form.email}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="partner-input partner-phone">
                        <select
                          name="countryCode"
                          value={form.countryCode}
                          onChange={handleChange}
                        >
                          {COUNTRY_CODES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.flag} {c.code}
                            </option>
                          ))}
                        </select>
                        <input
                          type="tel"
                          name="phone"
                          placeholder={`${t("becomePartner.phone")} *`}
                          value={form.phone}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-lock" />
                        <input
                          type="password"
                          name="password"
                          placeholder={`${t("becomePartner.password")} *`}
                          value={form.password}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-lock" />
                        <input
                          type="password"
                          name="confirm_password"
                          placeholder={`${t("becomePartner.confirmPassword")} *`}
                          value={form.confirm_password}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <button type="button" className="partner-submit" onClick={nextStep}>
                    {t("becomePartner.continueToDocuments")}
                  </button>
                </div>
              )}

              {/* Step 2: Documents */}
              {step === 2 && (
                <div className="step-content animate__animated animate__fadeIn">
                  <h5 className="partner-section-title">{t("becomePartner.verifyTitle")}</h5>
                  <p className="partner-verify-note">
                    {t("becomePartner.verifyNote")}
                  </p>

                  <div className="row g-4 mb-6">
                    <div className="col-md-4">
                      <label className="fs-6 fw-semibold mb-3 d-block">{t("becomePartner.businessLogo")}</label>
                      <ImageUpload
                        name="logo"
                        onImageChange={handleLogoChange}
                        defaultPreviewUrl={null}
                        maxSizeMB={MAX_FILE_SIZE_MB}
                      />
                      <small className="text-muted d-block mt-2">{t("becomePartner.maxSize2mb")}</small>
                    </div>
                    <div className="col-md-8">
                      <label className="fs-6 fw-semibold mb-3 d-block">{t("becomePartner.coverImages")}</label>
                      <FileDropzone
                        oldFiles={[]}
                        setFiles={setCoverImages}
                        updateOldImages={() => {}}
                        maxFiles={5}
                        maxSizeMB={MAX_FILE_SIZE_MB}
                      />
                    </div>
                  </div>

                  <h6 className="fs-7 fw-bold text-gray-700 mb-4 mt-8">{t("becomePartner.legalDocuments")}</h6>
                  {DOCUMENTS.map((doc) => (
                    <div
                      key={doc.key}
                      className="partner-upload"
                      onClick={() => fileRefs[doc.key].current.click()}
                    >
                      <div className="partner-upload-icon">
                        <i className="bi bi-upload" />
                      </div>
                      <div className="partner-upload-text">
                        <h6>{t(`becomePartner.docs.${doc.key}.title`)}{doc.required && <span className="text-danger"> *</span>}</h6>
                        <p>{documents[doc.key] ? documents[doc.key].name : t(`becomePartner.docs.${doc.key}.subtitle`)}</p>
                        <span>{t("becomePartner.fileHint")}</span>
                      </div>
                      <input
                        ref={fileRefs[doc.key]}
                        type="file"
                        accept=".jpg,.jpeg,.png,.pdf"
                        hidden
                        onChange={(e) => handleFile(doc.key, e.target.files[0])}
                      />
                    </div>
                  ))}

                  <div className="form-navigation">
                    <button type="button" className="partner-back" onClick={prevStep}>
                      {t("becomePartner.back")}
                    </button>
                    <button type="button" className="partner-submit" style={{marginTop: 0}} onClick={nextStep}>
                      {t("becomePartner.continueToBank")}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Bank Details */}
              {step === 3 && (
                <div className="step-content animate__animated animate__fadeIn">
                  <h5 className="partner-section-title">{t("becomePartner.bankTitle")}</h5>
                  <p className="partner-verify-note">
                    {t("becomePartner.bankNote")}
                  </p>
                  
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="partner-input">
                        <i className="bi bi-person" />
                        <input
                          type="text"
                          name="account_title"
                          placeholder={`${t("becomePartner.accountTitle")} *`}
                          value={form.account_title}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="partner-input">
                        <i className="bi bi-hash" />
                        <input
                          type="text"
                          name="account_number"
                          placeholder={`${t("becomePartner.accountNumber")} *`}
                          value={form.account_number}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="partner-input">
                        <i className="bi bi-credit-card" />
                        <input
                          type="text"
                          name="iban"
                          placeholder={`${t("becomePartner.iban")} *`}
                          value={form.iban}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-bank" />
                        <input
                          type="text"
                          name="bank_name"
                          placeholder={`${t("becomePartner.bankName")} *`}
                          value={form.bank_name}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="partner-input">
                        <i className="bi bi-geo-alt" />
                        <input
                          type="text"
                          name="city"
                          placeholder={t("becomePartner.city")}
                          value={form.city}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-navigation">
                    <button type="button" className="partner-back" onClick={prevStep} disabled={loading}>
                      {t("becomePartner.back")}
                    </button>
                    <button type="button" className="partner-submit" style={{marginTop: 0}} onClick={handleSubmit} disabled={loading}>
                      {loading ? <Loader size={20} color="white" text={t("becomePartner.submitting")} /> : t("becomePartner.completeRegistration")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: hero */}
          <div
            className="partner-hero-col"
            style={{ backgroundImage: `url(${heroImg})` }}
          >
            <div className="partner-hero-content">
              <h1>{t("becomePartner.heroTitle")}</h1>
              <p>
                {t("becomePartner.heroSubtitle")}
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />

      {showSuccessModal && (
        <div className="partner-success-overlay" dir={isRTL ? "rtl" : "ltr"}>
          <div className="partner-success-modal">
            <div className="partner-success-icon">
              <i className="bi bi-check-lg text-success" style={{fontSize: "44px"}} />
            </div>
            <h3 className="partner-success-title">{t("becomePartner.successModal.title")}</h3>
            <p className="partner-success-message">{t("becomePartner.successModal.message")}</p>
            <button type="button" className="partner-submit" onClick={handleCloseSuccessModal}>
              {t("becomePartner.successModal.button")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BecomePartner;
